import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import DateRangeFilter from './DateRangeFilter';
import { Download, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import SortableHeader, { useSort } from './SortableHeader';
import ChartContainer from './ChartContainer';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const ARAgingReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await reportService.getARaging(params);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching AR Aging:', err);
      setError('Failed to load AR Aging report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('days_overdue', 'desc');

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleExportCSV = () => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    reportService.downloadCSV(reportService.getARaging, params, 'ar_aging_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 rounded-lg" />)}
        </div>
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  const chartData = [
    { bucket: '1-15 days', amount: parseFloat(summary.bucket_1_15 || 0) },
    { bucket: '16-30 days', amount: parseFloat(summary.bucket_16_30 || 0) },
    { bucket: '31-60 days', amount: parseFloat(summary.bucket_31_60 || 0) },
    { bucket: '61+ days', amount: parseFloat(summary.bucket_61_plus || 0) },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">AR Aging Report</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard label="1-15 Days" value={formatCurrency(summary.bucket_1_15 || 0)} color="text-green-700" bg="bg-green-50" />
        <SummaryCard label="16-30 Days" value={formatCurrency(summary.bucket_16_30 || 0)} color="text-yellow-700" bg="bg-yellow-50" />
        <SummaryCard label="31-60 Days" value={formatCurrency(summary.bucket_31_60 || 0)} color="text-orange-700" bg="bg-orange-50" />
        <SummaryCard label="61+ Days" value={formatCurrency(summary.bucket_61_plus || 0)} color="text-red-700" bg="bg-red-50" />
        <SummaryCard label="Total Outstanding" value={formatCurrency(summary.total_outstanding || 0)} color="text-gray-900" bg="bg-gray-50" subtitle={`${summary.invoice_count || 0} invoices`} />
      </div>

      {/* Chart */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Aging Distribution</h3>
          {chartData.some(d => d.amount > 0) ? (
            <ChartContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#6B7280' }} width={60} />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                <Bar dataKey="amount" fill="#CA8A04" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm">No outstanding invoices</div>
          )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Invoice #" sortKey="invoice_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Case" sortKey="case_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Total" sortKey="total_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Paid" sortKey="paid_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Balance Due" sortKey="balance_due" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Days Overdue" sortKey="days_overdue" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Bucket" sortKey="aging_bucket" sortConfig={sortConfig} onSort={requestSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No outstanding invoices found</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.invoice_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.invoice_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.client_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.case_number || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.total_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.paid_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.balance_due)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{item.days_overdue}</td>
                  <td className="px-4 py-3"><BucketBadge bucket={item.aging_bucket} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color, bg, subtitle }) => (
  <div className={`${bg} rounded-lg p-4`}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const BucketBadge = ({ bucket }) => {
  const colors = {
    '1-15': 'bg-green-100 text-green-800',
    '16-30': 'bg-yellow-100 text-yellow-800',
    '31-60': 'bg-orange-100 text-orange-800',
    '61+': 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${colors[bucket] || 'bg-gray-100 text-gray-800'}`}>
      {bucket}
    </span>
  );
};

export default ARAgingReport;
